import {
  LintingStep,
  LintingSteps,
  QualityStep,
  QualitySteps,
  Step,
  TestResults,
  TestTestResults
} from "@cs124/questioner-types";
import assert from "assert";
import sortBy from "lodash/sortBy";
import moment from "moment-timezone";
import { Array, Boolean, InstanceOf, Literal, Number, Partial, Record, String } from "runtypes";
import { DeadlineCoordinates } from "./coordinates";
export const LatePolicy = Partial({
  latePenalty: Number,
  noPenaltyTotal: Number,
  isLate: Boolean
}).And(
  Record({
    policyType: Literal("lateperday"),
    latePerDay: Number.withConstraint((n) => n > 0)
  }).Or(
    Record({
      policyType: Literal("lateinterval"),
      lateDays: Number.withConstraint((n) => n >= 0)
    })
  )
);
export const SolveSpecification = Record({
  type: Literal("SOLVE"),
  total: Number
}).And(
  Partial({
    testing: Number,
    ktLint: Number,
    checkstyle: Number,
    complexity: Number,
    features: Number,
    lineCount: Number,
    recursion: Number,
    executioncount: Number,
    memoryAllocation: Number,
    coverage: Number,
    quality: Number,
    qualitySteps: String.withConstraint((s) => s === "all" || Array(QualityStep).guard(s.split(","))),
    latePolicy: LatePolicy
  })
).withConstraint(
  (p) => p.latePolicy?.policyType !== "lateperday" || p.latePolicy.latePerDay < p.total && p.total % p.latePolicy.latePenalty == 0
);
export const TestTestingSpecification = Record({
  type: Literal("TESTTESTING"),
  total: Number
}).And(
  Partial({
    testing: Number,
    ktLint: Number,
    checkstyle: Number,
    latePolicy: LatePolicy
  })
).withConstraint(
  (p) => p.latePolicy?.policyType !== "lateperday" || p.latePolicy.latePerDay < p.total && p.total % p.latePolicy.latePenalty == 0
);
export const StumperSpecification = Record({
  type: Literal("STUMPER"),
  total: Number,
  each: Number,
  skips: Number,
  checks: Number
}).And(
  Partial({
    latePolicy: LatePolicy
  })
).withConstraint(
  (p) => p.latePolicy?.policyType !== "lateperday" || p.latePolicy.latePerDay < p.total && p.total % p.latePolicy.latePenalty == 0
);
export const QuestionerSpecification = SolveSpecification.Or(TestTestingSpecification);
export const PointSpecification = SolveSpecification.Or(TestTestingSpecification).Or(StumperSpecification);
export const Points = Record({
  specification: PointSpecification,
  earned: PointSpecification
});
export const SavedPoints = DeadlineCoordinates.And(
  Record({
    semester: String,
    number: String,
    email: String,
    points: Points,
    updated: String.Or(InstanceOf(Date))
  })
);
const ScoringSteps = [...LintingSteps, ...QualitySteps, "testing", "quality"];
export const printPoints = (points) => {
  const earned = QuestionerSpecification.check(points.earned);
  const specification = QuestionerSpecification.check(points.specification);
  const latePenalty = earned.latePolicy?.policyType === "lateperday" ? `${earned.latePolicy.latePenalty} point late penalty` : "";
  if (pointsFullPointsWithoutPenalty(points)) {
    if (latePenalty) {
      return `${earned.total} / ${specification.total} (${latePenalty})`;
    } else {
      return `${earned.total} / ${specification.total}`;
    }
  }
  const sortedSteps = sortBy(
    ScoringSteps.filter((step) => Number.guard(specification[step])).filter((step) => {
      if (specification[step] < 0 && earned[step] < 0) {
        return true;
      } else if (specification[step] > 0 && (earned[step] === void 0 || earned[step] < specification[step])) {
        return true;
      } else {
        return false;
      }
    }),
    (step) => {
      if (specification[step] < 0 && earned[step] < 0) {
        return earned[step] * -1;
      } else if (specification[step] > 0 && (earned[step] === void 0 || earned[step] < specification[step])) {
        return specification[step];
      }
      assert(false);
    }
  );
  const explanations = [];
  let totalLost = 0;
  for (const step of sortedSteps) {
    if (earned[step] === -1 * specification.total || !earned[step] && specification[step] === specification.total) {
      totalLost = specification.total;
      explanations.push(`${step} failed`);
      break;
    } else {
      const lost = earned[step] < 0 ? -1 * earned[step] : specification[step] - (earned[step] || 0);
      totalLost += lost;
      const pluralized = lost === 1 ? "point" : "points";
      if (step === "quality") {
        const solveSpecification = SolveSpecification.check(specification);
        const qualitySteps = solveSpecification.qualitySteps.split(",");
        if (qualitySteps.length > 1) {
          const lastStep = qualitySteps.pop();
          explanations.push(`lost ${lost} ${pluralized} for ${qualitySteps.join(", ")} and ${lastStep}`);
        } else {
          explanations.push(`lost ${lost} ${pluralized} for ${qualitySteps[0]}`);
        }
      } else {
        explanations.push(`lost ${lost} ${pluralized} for ${step}`);
      }
    }
  }
  if (latePenalty && totalLost < specification.total) {
    explanations.unshift(latePenalty);
  }
  if (explanations.length === 0) {
    console.log(sortedSteps);
    console.log(JSON.stringify(points, null, 2));
    throw "No explanation found";
  }
  return `${earned.total} / ${specification.total} (${explanations.join(", ")})`;
};
export const scoreCheckstyleAndKtlint = (specification, results, which) => {
  if (!specification[which]) {
    return;
  }
  if (which === "checkstyle" && results.language !== "java") {
    return;
  }
  if (which === "ktlint" && results.language !== "kotlin") {
    return;
  }
  if (specification[which] > 0) {
    if (!results.complete[which]) {
      return 0;
    } else {
      return results.complete[which].errors.length > 0 ? 0 : specification[which];
    }
  } else {
    if (!results.complete[which]) {
      return specification[which];
    } else {
      return results.complete[which].errors.length > 0 ? specification[which] : 0;
    }
  }
};
export const scoreQuality = (specification, results, which) => {
  if (!specification[which]) {
    return;
  }
  if (specification[which] > 0) {
    if (!results.complete[which]) {
      return 0;
    } else {
      return results.complete[which].failed ? 0 : specification[which];
    }
  } else {
    if (!results.complete[which]) {
      return specification[which];
    } else {
      return results.complete[which].failed ? specification[which] : 0;
    }
  }
};
export const scoreAllQuality = (specification, results) => {
  if (!specification.quality || !specification.qualitySteps) {
    return;
  }
  const qualitySteps = specification.qualitySteps === "all" ? QualitySteps : Array(QualityStep).check(specification.qualitySteps.split(","));
  const qualityCount = [...qualitySteps].filter((step) => results.complete[step]?.failed === false).length;
  if (specification.quality > 0) {
    return qualityCount === qualitySteps.length ? specification.quality : 0;
  } else {
    return qualityCount === qualitySteps.length ? 0 : specification.quality;
  }
};
export const scoreSubmission = (now, deadline, results) => {
  const specification = QuestionerSpecification.check(deadline.points);
  let points;
  if (specification.type === "SOLVE") {
    assert(TestResults.guard(results));
    if (results.failedSteps.length > 0 || results.timeout) {
      points = Points.check({ specification, earned: { type: specification.type, total: 0 } });
    } else {
      const earned = { type: specification.type, total: 0 };
      for (const key of Object.keys(specification)) {
        if (LintingStep.guard(key)) {
          earned[key] = scoreCheckstyleAndKtlint(specification, results, key);
        } else if (QualityStep.guard(key)) {
          earned[key] = scoreQuality(specification, results, key);
        } else if (key === "testing") {
          earned[key] = results.succeeded === true ? specification.testing : 0;
        }
      }
      earned["quality"] = scoreAllQuality(specification, results);
      Object.keys(earned).forEach((key) => earned[key] === void 0 && delete earned[key]);
      earned.total = Math.max(
        Array(Number).check(
          Object.keys(earned).filter((k) => k === "quality" || Step.guard(k)).map((k) => Number.check(earned[k]))
        ).reduce((sum, v) => sum + v, 0),
        0
      );
      PointSpecification.check(earned);
      points = Points.check({ specification, earned });
    }
  } else if (specification.type === "TESTTESTING") {
    assert(TestTestResults.guard(results));
    if (results.failedSteps.length > 0 || results.timeout) {
      points = Points.check({ specification, earned: { type: specification.type, total: 0 } });
    } else {
      const earned = { type: specification.type, total: 0 };
      for (const key of Object.keys(specification)) {
        if (LintingStep.guard(key)) {
          earned[key] = scoreCheckstyleAndKtlint(specification, results, key);
        } else if (QualityStep.guard(key)) {
          earned[key] = scoreQuality(specification, results, key);
        } else if (key === "testing") {
          const { testTesting } = results.complete;
          earned[key] = testTesting.identifiedSolution !== true ? 0 : results.succeeded === true ? specification.total : testTesting.incorrect + 1 === testTesting.total ? 0 : Math.round(specification.total * (testTesting.correct / testTesting.total));
        }
      }
      PointSpecification.check(earned);
      earned.total = Math.max(
        Array(Number).check(Object.values(earned).filter((v) => Number.guard(v))).reduce((sum, v) => sum + v, 0),
        0
      );
      points = Points.check({ specification, earned });
    }
  }
  pointsSetLatePenalty(now, deadline, points);
  return points;
};
export const pointsSetLatePenalty = (now, deadline, points) => {
  const specification = QuestionerSpecification.check(deadline.points);
  if (!specification.latePolicy) {
    return;
  }
  const earned = QuestionerSpecification.check(points.earned);
  earned.latePolicy = { ...specification.latePolicy };
  const { penalty, isLate } = pointsGetLatePenalty(now, deadline);
  if (isLate) {
    earned.latePolicy.isLate = isLate;
    earned.latePolicy.latePenalty = penalty;
    earned.latePolicy.noPenaltyTotal = earned.total;
    const newTotal = Math.max(0, earned.total - penalty);
    if (newTotal !== earned.total) {
      earned.total = newTotal;
    }
  }
};
export const pointsGetLatePenalty = (now, deadline) => {
  const specification = PointSpecification.check(deadline.points);
  assert(specification.latePolicy !== void 0);
  const isLate = moment(now).isAfter(moment(deadline.end));
  if (!isLate) {
    return { penalty: 0, isLate };
  }
  if (specification.latePolicy.policyType === "lateperday") {
    const penalty = Math.min(
      Math.ceil(moment(now).diff(moment(deadline.end), "days", true)) * specification.latePolicy.latePerDay,
      specification.total
    );
    return { penalty, isLate };
  } else {
    const penalty = moment(now).isAfter(moment(deadline.end).add(specification.latePolicy.lateDays, "days")) ? specification.total : 0;
    return { penalty, isLate };
  }
};
export const pointsGetPointsAvailable = (now, deadline) => {
  const specification = PointSpecification.check(deadline.points);
  if (!specification.latePolicy) {
    return moment(now).isBefore(moment(deadline.end)) ? specification.total : 0;
  } else {
    return Math.max(deadline.points.total - pointsGetLatePenalty(now, deadline).penalty, 0);
  }
};
export const pointsFullPointsWithoutPenalty = (points) => {
  const earned = QuestionerSpecification.check(points.earned);
  const specification = QuestionerSpecification.check(points.specification);
  return earned.total === specification.total;
};
export const pointsHasFullLatePenalty = (points) => {
  const earned = QuestionerSpecification.check(points.earned);
  const specification = QuestionerSpecification.check(points.specification);
  return (earned.latePolicy?.latePenalty ?? 0) >= specification.total;
};
//# sourceMappingURL=points.js.map
